import { Fragment, forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { DropdownNext, DropdownNextProps, Panel, Shortcut } from '@desygner/ui-common-components';

import { FloatingToolbarItem } from './FloatingToolbar.type';
import { FloatingToolbarButton } from './components/FloatingToolbarButton';

export interface FloatingToolbarProps {
  items: FloatingToolbarItem[];
  moreOptions: FloatingToolbarItem[];
  onClick: (item: FloatingToolbarItem | null) => (event: FloatingToolbarClick) => void;
  onShow?: (bbox: DOMRect) => void;
}

export interface FloatingToolbarClick {
  action: string;
  payload: unknown;
}

export const FloatingToolbar = memo(
  forwardRef<HTMLDivElement, FloatingToolbarProps>(function FloatingToolbar(
    { items, moreOptions, onClick, onShow },
    ref
  ) {
    const toolbarRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => toolbarRef.current as HTMLDivElement);

    useEffect(() => {
      if (toolbarRef?.current) {
        onShow?.(toolbarRef.current.getBoundingClientRect());
      }
    }, []);

    const hiddenOptions: DropdownNextProps['options'] = useMemo(() => {
      return moreOptions
        .map((item) => {
          const dropdownItems: DropdownNextProps['options'] = [
            {
              type: 'option',
              label: item.label ?? '',
              value: item.id,
              disabled: item.data?.disabled || item.disabled,
              icon: item.icon,
              children:
                Array.isArray(item.items) && item.items.length > 0
                  ? item.items?.map((element) => ({
                      label: element.label ?? '',
                      value: element.id,
                      action: element.action,
                      icon: element.icon,
                      onClick: () =>
                        onClick(element)({
                          action: element.action,
                          payload: {
                            ...element,
                            bbox: toolbarRef?.current?.getBoundingClientRect(),
                          },
                        }),
                    }))
                  : undefined,
              onClick: () =>
                onClick(item)({
                  action: item.action,
                  payload: {
                    ...item,
                    bbox: toolbarRef?.current?.getBoundingClientRect(),
                  },
                }),
              extra: item.shortcut && <Shortcut value={item.shortcut} />,
            },
          ];

          item.isSeparator && dropdownItems.push({ type: 'divider' });
          return dropdownItems;
        })
        .flat();
    }, [moreOptions, onClick]);

    if (items.length === 0) {
      return null;
    }

    return (
      <div ref={toolbarRef}>
        <Panel>
          <Panel.Content>
            {items.map(({ label, ...item }) => {
              return (
                <Fragment key={item.id}>
                  {item.render ? (
                    item.render(onClick(item), item.data)
                  ) : (
                    <FloatingToolbarButton
                      item={{
                        ...item,
                        tooltip: label,
                      }}
                      onClick={onClick(item)}
                      toolbarRef={toolbarRef}
                    />
                  )}
                  {item.isSeparator && <Panel.Divider />}
                </Fragment>
              );
            })}
          </Panel.Content>

          {hiddenOptions.length > 0 && (
            <DropdownNext trigger={'click'} placement={'rightTop'} options={hiddenOptions}>
              <Panel.Content>
                <FloatingToolbarButton
                  item={{
                    id: 'more',
                    action: 'more',
                    icon: {
                      name: 'more-options---20',
                    },
                    isSeparator: false,
                  }}
                  toolbarRef={toolbarRef}
                />
              </Panel.Content>
            </DropdownNext>
          )}
        </Panel>
      </div>
    );
  })
);
